import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';
import {
  analyticsAPI,
  AnalyticsOverview,
  ClassRow,
  TrendPoint,
  leaderboardAPI,
  ClassLeaderboard,
} from '../../services/api';

const REFRESH_MS = 45000; // auto-refresh cadence for "real-time" feel

const KPIS: { key: keyof AnalyticsOverview; label: string; sub?: string; icon: string; color: string }[] = [
  { key: 'students_active', label: 'Students Active', sub: 'read, quiz, like or sign-in', icon: '🎓', color: 'from-indigo-500 to-indigo-600' },
  { key: 'articles_read', label: 'Articles Read', icon: '📰', color: 'from-amber-500 to-amber-600' },
  { key: 'likes', label: 'Likes', icon: '❤️', color: 'from-teal-500 to-teal-600' },
  { key: 'quiz_players', label: 'Quiz Players', icon: '🎯', color: 'from-pink-500 to-pink-600' },
];

const AnalyticsDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const today = format(new Date(), 'yyyy-MM-dd');
  const [date, setDate] = useState(today);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [classes, setClasses] = useState<ClassRow[]>([]);
  const [trend, setTrend] = useState<TrendPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Monthly class leaderboard (teacher view)
  const [lbClass, setLbClass] = useState<number | ''>('');
  const [lb, setLb] = useState<ClassLeaderboard | null>(null);
  const [lbLoading, setLbLoading] = useState(false);

  const dateRef = useRef(date);
  dateRef.current = date;

  const load = useCallback(async (isBackground = false) => {
    if (!isBackground) setLoading(true);
    setError(null);
    try {
      const d = dateRef.current;
      const [ov, cl, tr] = await Promise.all([
        analyticsAPI.getOverview(d),
        analyticsAPI.getByClass(d),
        analyticsAPI.getTrend(10),
      ]);
      setOverview(ov);
      setClasses(cl.classes);
      setTrend(tr.series);
      setLastUpdated(new Date());
    } catch (e: any) {
      setError(e?.response?.status === 403
        ? 'You do not have permission to view analytics.'
        : 'Could not load analytics. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Reload when the selected date changes
  useEffect(() => { load(); }, [date, load]);

  // Auto-refresh (background, no spinner) when enabled and viewing today
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => load(true), REFRESH_MS);
    return () => clearInterval(id);
  }, [autoRefresh, load]);

  // Default the leaderboard to the first class once the class list loads.
  useEffect(() => {
    if (lbClass === '' && classes.length) setLbClass(classes[0].class_standard);
  }, [classes, lbClass]);

  // Load the leaderboard whenever the chosen class changes (current month).
  useEffect(() => {
    if (lbClass === '') return;
    setLbLoading(true);
    leaderboardAPI.getClass(Number(lbClass))
      .then(setLb)
      .catch(() => setLb(null))
      .finally(() => setLbLoading(false));
  }, [lbClass]);

  const handleLogout = () => { logout(); navigate('/auth'); };

  const trendMax = Math.max(1, ...trend.map((t) => t.students_active));
  const isToday = date === today;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header
        className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg"
        // clears the iPhone status bar; resolves to 0 where there is no inset
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 rounded-lg hover:bg-white/15 transition-colors flex-shrink-0"
              title="Back to articles"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <div className="bg-white p-1.5 rounded-xl shadow flex-shrink-0">
              <img src="/knoozy-logo.png" alt="Knoozy" className="h-9 w-9 object-contain" />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-bold leading-tight truncate">Analytics Dashboard</h1>
              <p className="text-xs sm:text-sm opacity-90 truncate">
                {overview?.school?.name || 'Your School'}
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg hover:bg-white/15 transition-colors flex-shrink-0"
          >
            <ArrowRightOnRectangleIcon className="h-5 w-5" />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Controls */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-slate-600">Date</label>
            <input
              type="date"
              value={date}
              max={today}
              onChange={(e) => setDate(e.target.value)}
              className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
            {!isToday && (
              <button onClick={() => setDate(today)}
                className="text-sm text-indigo-600 hover:underline font-medium">Today</button>
            )}
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
            <input type="checkbox" checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" />
            Auto-refresh
          </label>

          <button onClick={() => load()}
            className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-white border border-slate-300 hover:bg-slate-50 text-slate-700">
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>

          {lastUpdated && (
            <span className="text-xs text-slate-400 ml-auto">
              Updated {format(lastUpdated, 'h:mm:ss a')}
            </span>
          )}
        </div>

        {error && (
          <div className="mb-6 rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
            {error}
          </div>
        )}

        {loading && !overview ? (
          <div className="text-center py-20 text-slate-400">
            <div className="text-5xl mb-3 animate-bounce">📊</div>
            Loading analytics…
          </div>
        ) : (
          <>
            {/* KPI cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4">
              {KPIS.map((k) => (
                <div key={k.key} className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4">
                  <div className={`inline-flex items-center justify-center h-9 w-9 rounded-xl bg-gradient-to-br ${k.color} text-white text-lg mb-2`}>
                    {k.icon}
                  </div>
                  <div className="text-2xl sm:text-3xl font-extrabold text-slate-800 leading-none">
                    {(overview?.[k.key] as number ?? 0).toLocaleString()}
                  </div>
                  <div className="text-xs font-medium text-slate-600 mt-1">{k.label}</div>
                  {k.sub && <div className="text-[10px] text-slate-400">{k.sub}</div>}
                </div>
              ))}
            </div>

            {/* Reach strip */}
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 rounded-2xl px-5 py-3 mb-6 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
              <span className="text-slate-600">
                <span className="font-bold text-indigo-700">{(overview?.cumulative_students ?? 0).toLocaleString()}</span> unique students reached all-time
              </span>
              {isToday && (
                <span className="ml-auto inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600">
                  <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" /> Live · today
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 gap-6">
              {/* Class-wise table */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 text-left">
                <h2 className="font-bold text-slate-800 mb-1">Class-wise Engagement</h2>
                <p className="text-xs text-slate-400 mb-4">
                  {date === today ? 'Today' : format(new Date(date), 'EEE, d MMM yyyy')}
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm table-fixed">
                    <colgroup>
                      <col className="w-[28%]" />
                      <col className="w-[24%]" />
                      <col className="w-[24%]" />
                      <col className="w-[24%]" />
                    </colgroup>
                    <thead>
                      <tr className="text-[11px] text-slate-400 uppercase tracking-wider">
                        <th className="text-left font-semibold pb-3 pl-4 border-b-2 border-slate-100">Class</th>
                        <th className="text-center font-semibold pb-3 border-b-2 border-slate-100">Students</th>
                        <th className="text-center font-semibold pb-3 border-b-2 border-slate-100">Reads</th>
                        <th className="text-center font-semibold pb-3 border-b-2 border-slate-100">Quiz</th>
                      </tr>
                    </thead>
                    <tbody>
                      {classes.map((c, i) => (
                        <tr key={c.class_standard} className={`${i % 2 ? 'bg-slate-50/60' : ''} hover:bg-indigo-50/50 transition-colors`}>
                          <td className="py-3.5 pl-4 rounded-l-lg">
                            <span className="inline-flex items-center gap-2">
                              <span className="text-slate-500 font-medium">Class</span>
                              <span className="inline-flex items-center justify-center min-w-[2.2rem] h-7 px-2 rounded-lg bg-indigo-100 text-indigo-700 font-extrabold text-xs">
                                {c.class_label}
                              </span>
                            </span>
                          </td>
                          <td className="py-3.5 text-center font-bold text-indigo-600 text-base tabular-nums">{c.students_active.toLocaleString()}</td>
                          <td className="py-3.5 text-center font-semibold text-slate-600 tabular-nums">{c.articles_read.toLocaleString()}</td>
                          <td className="py-3.5 text-center font-semibold text-slate-600 tabular-nums rounded-r-lg">{c.quiz_players.toLocaleString()}</td>
                        </tr>
                      ))}
                      {classes.length === 0 && (
                        <tr><td colSpan={4} className="text-center py-8 text-slate-400">No activity for this day yet.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Trend */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 mt-6 text-left">
              <h2 className="font-bold text-slate-800 mb-1">Daily Active Students</h2>
              <p className="text-xs text-slate-400 mb-4">Last {trend.length} days</p>
              <div className="flex items-end gap-1 sm:gap-2 h-40 overflow-hidden">
                {trend.map((t) => {
                  const h = Math.max(4, (t.students_active / trendMax) * 100);
                  const highlight = t.date === date;
                  return (
                    <div key={t.date} className="flex-1 min-w-0 flex flex-col items-center justify-end h-full group">
                      <div className="text-[10px] font-semibold text-slate-500 mb-1">{t.students_active}</div>
                      <div className="w-full flex items-end px-0.5" style={{ height: '100%' }}>
                        <div
                          className={`w-full rounded-t-md ${highlight ? 'bg-indigo-600' : 'bg-indigo-300 group-hover:bg-indigo-400'}`}
                          style={{ height: `${h}%` }}
                          title={`${t.date}: ${t.students_active} students, ${t.articles_read} reads`}
                        />
                      </div>
                      <div className="text-[9px] text-slate-400 mt-1 truncate w-full text-center">
                        {format(new Date(t.date), 'd/M')}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Monthly class leaderboard */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 mt-6 text-left">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
                <h2 className="font-bold text-slate-800">🏆 Class Leaderboard</h2>
                <select
                  value={lbClass}
                  onChange={(e) => setLbClass(e.target.value ? Number(e.target.value) : '')}
                  className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  {classes.map((c) => (
                    <option key={c.class_standard} value={c.class_standard}>Class {c.class_label}</option>
                  ))}
                </select>
              </div>
              <p className="text-xs text-slate-400 mb-4">
                {lb ? `Top 3 · ${lb.month}` : 'Top 3 this month'}
              </p>

              {lbLoading ? (
                <div className="text-center py-8 text-slate-400 text-sm">Loading…</div>
              ) : lb && lb.entries.length ? (
                <div className="overflow-x-auto">
                  <table className="w-full max-w-sm mx-auto text-sm border-collapse rounded-xl overflow-hidden border border-slate-100">
                    <thead>
                      <tr className="bg-slate-50 text-[11px] text-slate-500 uppercase tracking-wider">
                        <th className="text-center font-bold py-3 w-20">Rank</th>
                        <th className="text-left font-bold py-3 pr-4">Student</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lb.entries.filter((e) => e.rank <= 3).map((e, i) => (
                        <tr key={i} className={`border-t border-slate-100 ${i % 2 ? 'bg-slate-50/40' : 'bg-white'}`}>
                          <td className="py-3 w-20 text-center align-middle text-xl leading-none">
                            {e.rank === 1 ? '🥇' : e.rank === 2 ? '🥈' : '🥉'}
                          </td>
                          <td className="py-3 pr-4 text-left align-middle font-semibold text-slate-700">{e.name}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400 text-sm">No activity in this class yet this month.</div>
              )}
            </div>

            <p className="text-center text-xs text-slate-300 mt-8">
              Knoozy · figures update automatically {autoRefresh ? 'every 45s' : '(auto-refresh off)'}
            </p>
          </>
        )}
      </main>
    </div>
  );
};

export default AnalyticsDashboard;
